import React, { useState, useEffect, lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useImmer } from 'use-immer'
import ReactPaginate from 'react-paginate'
import { Button, Col, Row, Table } from 'react-bootstrap'
import Select from 'react-select'
import Swal from 'sweetalert2'
import {
  allCrudNames,
  allPages,
  checkPageAccess
} from '../../../../lib/AllPageNames'
import axiosProvider from '../../../../lib/AxiosProvider'
import useDebounce from '../../../../lib/useDebounce'
import Loader from '../../../../components/Loader'
import CustomToast from '../../../../components/Toast/CustomToast'
import {
  _percentageRegex_,
  _SwalDelete,
  _exception
} from '../../../../lib/exceptionMessage'
import DeleteIcon from '../../../../components/AllSvgIcon/DeleteIcon'
import EditIcon from '../../../../components/AllSvgIcon/EditIcon'
import { customStyles } from '../../../../components/customStyles'
import { calculatePageRange, encodedSearchText, showToast } from '../../../../lib/AllGlobalFunction'
import { pageRangeDisplayed } from '../../../../lib/AllStaticVariables'
import SearchBox from '../../../../components/Searchbox'

const SellerWiseCommisionForm = lazy(() => import('./SellerWiseCommisionForm'))

const SellerWiseCommissionList = () => {
  const initVal = {
    catID: null,
    sellerID: null,
    amountValue: ''
  }
  const [modalShow, setModalShow] = useState(false)
  const [data, setData] = useState()
  const [allState, setAllState] = useImmer({ category: [], sellerDetails: [] })
  const [searchText, setSearchText] = useState()
  const [initialValues, setInitialValues] = useState(initVal)
  const [loading, setLoading] = useState(false)
  const debounceSearchText = useDebounce(searchText, 500)
  const { userInfo } = useSelector((state) => state?.user)
  const location = useLocation()
  const { pageAccess } = useSelector((state) => state.user)

  const [filterDetails, setFilterDetails] = useImmer({
    pageSize: 10,
    pageIndex: 1,
    searchText: '',
    sellerID: ''
  })
  const [toast, setToast] = useState({
    show: false,
    text: null,
    variation: null
  })

  const handlePageClick = (event) => {
    setFilterDetails((draft) => {
      draft.pageIndex = event.selected + 1
    })
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'SellerWiseCommission/search',
        queryString: `?searchText=${encodedSearchText(
          filterDetails?.searchText
        )}&pageIndex=${filterDetails?.pageIndex}&pageSize=${
          filterDetails?.pageSize
        }&sellerID=${filterDetails?.sellerID}`
      })
      setLoading(false)

      if (response?.status === 200) {
        setData(response)
        if (!allState?.sellerDetails?.length) {
          const response = await axiosProvider({
            method: 'GET',
            endpoint: 'SellerData/bindActiveSeller'
          })

          if (response?.status === 200) {
            setAllState((draft) => {
              draft.sellerDetails = response?.data?.data
            })
          }
        }
      }
    } catch {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    setLoading(true)
    try {
      const response = await axiosProvider({
        method: 'DELETE',
        endpoint: `SellerWiseCommission?id=${id}`,
        userId: userInfo?.userId,
        location: location?.pathname
      })

      if (response?.status === 200) {
        fetchData()
      }
      setLoading(false)
      showToast(toast, setToast, response)
    } catch (error) {
      setLoading(false)
      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  useEffect(() => {
    if (debounceSearchText) {
      setFilterDetails((draft) => {
        draft.searchText = debounceSearchText
        draft.pageIndex = 1
      })
    } else {
      setFilterDetails((draft) => {
        draft.searchText = ''
        draft.pageIndex = 1
      })
    }
  }, [debounceSearchText])

  useEffect(() => {
    fetchData()
  }, [filterDetails])

  return (
    <div>
      <Row className='mb-3'>
        <Col md={8}>
          <div className='d-flex align-items-center gap-3'>
            <SearchBox
              placeholderText={'Search by category'}
              searchclassnamewrapper={'searchbox-wrapper'}
              value={searchText}
              onChange={(e) => {
                setSearchText(e?.target?.value)
              }}
            />

            <div className='col-md-3 input-file-wrapper'>
              <Select
                name='sellerID'
                isClearable
                placeholder='Seller'
                styles={customStyles}
                menuPortalTarget={document.body}
                value={
                  filterDetails?.sellerID && {
                    value: filterDetails?.sellerID,
                    label: filterDetails?.sellerName
                  }
                }
                options={
                  allState?.sellerDetails &&
                  allState?.sellerDetails?.map(({ userID, displayName }) => ({
                    value: userID,
                    label: displayName
                  }))
                }
                onChange={async (e) => {
                  if (e?.value) {
                    setFilterDetails((draft) => {
                      draft.pageSize = 10
                      draft.pageIndex = 1
                      draft.sellerID = e?.value
                      draft.sellerName = e?.label
                    })
                  } else {
                    setFilterDetails((draft) => {
                      draft.pageSize = 10
                      draft.pageIndex = 1
                      draft.sellerID = ''
                      draft.sellerName = ''
                    })
                  }
                }}
              />
            </div>

            <div className='d-flex align-items-center'>
              <label className='me-1'>Show</label>
              <select
                name='dataget'
                id='parpageentries'
                className='form-select me-1'
                onChange={(e) => {
                  setFilterDetails((draft) => {
                    draft.pageSize = e?.target?.value
                    draft.pageIndex = 1
                  })
                }}
              >
                <option value='10'>10</option>
                <option value='25'>25</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
                <option value='200'>200</option>
                <option value='500'>500</option>
              </select>
            </div>

            <div className='page-range'>
              {calculatePageRange({
                ...filterDetails,
                recordCount: data?.data?.pagination?.recordCount ?? 0
              })}
            </div>
          </div>
        </Col>
        <Col md={4}>
          {checkPageAccess(
            pageAccess,
            allPages?.manageCommission,
            allCrudNames?.write
          ) && (
            <Button
              variant='warning'
              className='d-flex align-items-center gap-2 fw-semibold ms-auto'
              onClick={async () => {
                setModalShow(true)
                setInitialValues(initVal)

                if (!allState?.category?.length) {
                  try {
                    const response = await axiosProvider({
                      method: 'GET',
                      endpoint: 'MainCategory/getAllCategory',
                      queryString: '?pageSize=0&pageIndex=0'
                    })

                    if (response?.status === 200) {
                      setAllState((draft) => {
                        draft.category = response?.data?.data
                      })
                    }
                  } catch {}
                }

                if (!allState?.sellerDetails?.length) {
                  try {
                    const response = await axiosProvider({
                      method: 'GET',
                      endpoint: 'SellerData/bindActiveSeller'
                    })

                    if (response?.status === 200) {
                      setAllState((draft) => {
                        draft.sellerDetails = response?.data?.data
                      })
                    }
                  } catch {}
                }
              }}
            >
              <i className='m-icon m-icon--plusblack'></i>
              Create
            </Button>
          )}
        </Col>
      </Row>

      <Table responsive hover className='align-middle table-list'>
        <thead>
          <tr>
            <th>Seller Name</th>
            <th>Categories</th>
            {/* <th>Charges In</th> */}
            <th>Amount</th>
            {checkPageAccess(pageAccess, allPages?.manageCommission, [
              allCrudNames?.update,
              allCrudNames?.delete
            ]) && <th className='text-center'>Action</th>}
          </tr>
        </thead>
        <tbody>
          {data?.data?.data?.length > 0 ? (
            data?.data?.data?.map((data, index) => (
              <tr key={data?.id}>
                <td>{data?.sellerName}</td>
                <td>{data?.categoryName}</td>
                {/* <td>{data?.chargesIn}</td> */}
                <td>{data?.amountValue}%</td>
                {checkPageAccess(pageAccess, allPages?.manageCommission, [
                  allCrudNames?.update,
                  allCrudNames?.delete
                ]) && (
                  <td>
                    <div className='d-flex gap-2 justify-content-center align-items-center'>
                      {checkPageAccess(
                        pageAccess,
                        allPages?.manageCommission,
                        allCrudNames?.update
                      ) && (
                        <span
                          onClick={async () => {
                            setInitialValues(data)
                            setModalShow(!modalShow)
                          }}
                        >
                          <EditIcon bg='bg' />
                        </span>
                      )}
                      {checkPageAccess(
                        pageAccess,
                        allPages?.manageCommission,
                        allCrudNames?.delete
                      ) && (
                        <span
                          onClick={() => {
                            Swal.fire({
                              title: _SwalDelete.title,
                              text: _SwalDelete.text,
                              icon: _SwalDelete.icon,
                              showCancelButton: _SwalDelete.showCancelButton,
                              confirmButtonColor:
                                _SwalDelete.confirmButtonColor,
                              cancelButtonColor: _SwalDelete.cancelButtonColor,
                              confirmButtonText: _SwalDelete.confirmButtonText,
                              cancelButtonText: _SwalDelete.cancelButtonText
                            }).then((result) => {
                              if (result.isConfirmed) {
                                handleDelete(data?.id)
                              } else if (result.isDenied) {
                              }
                            })
                          }}
                        >
                          <DeleteIcon bg='bg' />
                        </span>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className='text-center'>
                {data?.data?.message}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <ReactPaginate
        className='list-inline m-cst--pagination d-flex justify-content-end gap-1'
        breakLabel='...'
        nextLabel=''
        onPageChange={handlePageClick}
        pageRangeDisplayed={pageRangeDisplayed}
        pageCount={data?.data?.pagination?.pageCount ?? 0}
        previousLabel=''
        renderOnZeroPageCount={null}
        forcePage={filterDetails?.pageIndex - 1}
      />

      <Suspense fallback={<Loader />}>
        {modalShow && (
          <SellerWiseCommisionForm
            modalShow={modalShow}
            initialValues={initialValues}
            setModalShow={setModalShow}
            loading={loading}
            setLoading={setLoading}
            fetchData={fetchData}
            toast={toast}
            setToast={setToast}
            allState={allState}
          />
        )}
      </Suspense>

      {loading && !modalShow && <Loader />}

      {toast?.show && !modalShow && (
        <CustomToast text={toast?.text} variation={toast?.variation} />
      )}
    </div>
  )
}

export default SellerWiseCommissionList
